/**
 * The CV engine: runs the detectors on live frames and keeps the history.
 *
 * This is the piece that makes it a demonstration of *collection* rather than a
 * row of one-shot readouts. Each camera gets a ring buffer of samples; the wall
 * draws sparklines from it; and the aggregate feeds a counter that only ever
 * goes up while the page is open. Nothing here is persisted or sent anywhere —
 * the history is deliberately per-session, because a browser tab that quietly
 * accumulated and uploaded imagery would be a different and much more dangerous
 * product than one that shows you what it can see.
 *
 * The scheduler is intentionally dumb: one `requestIdleCallback`-ish timer, a
 * round-robin over cameras, at most one camera analysed per tick. Analysing
 * every tile every frame would starve the map. A dropped tick is recorded, not
 * hidden — the panel shows the sample count so a gap is visible.
 */

import { estimateHaze, assessFrame, HAZE_WORK_WIDTH, downscaleRgba, type HazeResult, type Rgb, type FrameHealth } from "./haze";
import { diffFrames, type TrafficResult } from "./traffic";

export const RING_CAPACITY = 48;

export interface CvSample {
  at: number;
  haze: HazeResult | null;
  traffic: TrafficResult | null;
  /** Why the frame could not be judged, when it could not be. */
  health?: FrameHealth;
}

export interface CvTrack {
  cameraId: string;
  samples: CvSample[];
  /** Last reduced frame, kept so the next tick can difference against it. */
  previous: Rgb | null;
  lastHaze: HazeResult | null;
  lastTraffic: TrafficResult | null;
  /** Latest frame-health verdict. A dead camera is a first-class reading. */
  lastHealth: FrameHealth | null;
  /** Consecutive ticks that produced no usable reading (tainted/failed). */
  misses: number;
}

export interface CvEngineState {
  tracks: Map<string, CvTrack>;
  cursor: number;
  totalAnalysed: number;
  ticks: number;
}

export function createEngine(): CvEngineState {
  return { tracks: new Map(), cursor: 0, totalAnalysed: 0, ticks: 0 };
}

function trackFor(state: CvEngineState, cameraId: string): CvTrack {
  let t = state.tracks.get(cameraId);
  if (!t) {
    t = { cameraId, samples: [], previous: null, lastHaze: null, lastTraffic: null, lastHealth: null, misses: 0 };
    state.tracks.set(cameraId, t);
  }
  return t;
}

/** Reduce a full-resolution frame once, then analyse the small copy. */
function reduce(rgba: Uint8ClampedArray, width: number, height: number): Rgb {
  return downscaleRgba(rgba, width, height, HAZE_WORK_WIDTH);
}

/**
 * Analyse one camera's freshly-decoded frame. Pure apart from mutating `state`.
 * Split from the scheduling so it can be tested with synthetic frames.
 */
export function analyseFrame(
  state: CvEngineState,
  cameraId: string,
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
  at: number,
): CvTrack {
  const track = trackFor(state, cameraId);
  const small = reduce(rgba, width, height);
  // Health first: a vendor "No signal" card is not a clear day, and diffing it
  // against the last frame would report the text as motion.
  const health = assessFrame(small);
  const haze = health.usable ? estimateHaze(small) : null;
  // Only diff against the previous frame of the SAME size, else it is a resize
  // artefact and would read as a total scene change.
  // Only diff live frames. A dead card has no scene, so its pixels are not
  // evidence that anything moved.
  const traffic = track.previous && health.usable ? diffFrames(track.previous, small) : null;
  track.previous = health.usable ? small : null;
  track.lastHaze = haze;
  track.lastTraffic = traffic;
  track.lastHealth = health;
  track.misses = haze ? 0 : track.misses + 1;

  track.samples.push({ at, haze, traffic, health });
  if (track.samples.length > RING_CAPACITY) track.samples.shift();
  state.totalAnalysed++;
  return track;
}

/** Advance the round-robin by one camera. Returns the id analysed, or null. */
export function step(
  state: CvEngineState,
  cameraIds: string[],
  loadFrame: (id: string) => { rgba: Uint8ClampedArray; width: number; height: number } | null,
  at: number,
): string | null {
  state.ticks++;
  if (cameraIds.length === 0) return null;
  const id = cameraIds[state.cursor % cameraIds.length];
  state.cursor = (state.cursor + 1) % cameraIds.length;
  const frame = loadFrame(id);
  if (!frame) {
    const track = trackFor(state, id);
    track.misses++;
    return null;
  }
  analyseFrame(state, id, frame.rgba, frame.width, frame.height, at);
  return id;
}

/** AOD over the ring, for the wall sparkline. Sourced quantity, not a scale. */
export function hazeSeries(track: CvTrack | undefined): number[] {
  if (!track) return [];
  return track.samples.map((s) => s.haze?.aod ?? 0);
}

/** Moving-object counts over the ring — what "traffic over time" looks like. */
export function motionSeries(track: CvTrack | undefined): number[] {
  if (!track) return [];
  return track.samples.map((s) => s.traffic?.blobs.length ?? 0);
}

export interface CvSummary {
  tracked: number;
  samples: number;
  haziest: { id: string; aod: number } | null;
  busiest: { id: string; motion: number; blobs: number } | null;
}

export function summarise(state: CvEngineState): CvSummary {
  let tracked = 0;
  let samples = 0;
  let haziest: CvSummary["haziest"] = null;
  let busiest: CvSummary["busiest"] = null;
  for (const t of state.tracks.values()) {
    if (t.samples.length > 0) tracked++;
    samples += t.samples.length;
    if (t.lastHaze && (!haziest || t.lastHaze.aod > haziest.aod)) {
      haziest = { id: t.cameraId, aod: t.lastHaze.aod };
    }
    if (t.lastTraffic && (!busiest || t.lastTraffic.motion > busiest.motion)) {
      busiest = {
        id: t.cameraId,
        motion: t.lastTraffic.motion,
        blobs: t.lastTraffic.blobs.length,
      };
    }
  }
  return { tracked, samples, haziest, busiest };
}

/** Serialise for the panel's "what did the CV actually measure" disclosure. */
export function methodDisclosure(): Array<{ signal: string; method: string; truth: string }> {
  return [
    {
      signal: "Haze",
      method: "dark-channel prior (He et al., CVPR 2009)",
      truth: "image estimate, not a PM2.5 reading",
    },
    {
      signal: "Motion",
      method: "frame differencing + 4-connected components",
      truth: "counts moving regions; cannot classify or measure speed",
    },
    {
      signal: "Glare",
      method: "bright, low-saturation pixel fraction",
      truth: "headlights and standing water are the same signal",
    },
  ];
}
